// Personalization-step configuration for each monogram type, keyed by the exact
// type name stored on the `monogram_component` metaobject and listed in
// type-selector grid order. Each entry declares its label key, input `mode`
// (INITIALS | LINES), the `fields` it captures, and — for INITIALS — the
// `previewLayers` that map each entered value to the letter position image used
// to build the live preview.

export const INITIALS = "initials";
export const LINES = "lines";

export const INITIAL_MAX_LENGTH = 1;
export const LINE_MAX_LENGTH = 18;

export const MONOGRAM_TYPES = {
  "Three Letter Initials": {
    labelKey: "three_letter_initials",
    mode: INITIALS,
    fields: [
      {
        key: "left",
        labelKey: "left",
        defaultKey: "left",
        transform: "uppercase",
      },
      {
        key: "middle",
        labelKey: "middle",
        defaultKey: "middle",
        transform: "uppercase",
      },
      {
        key: "right",
        labelKey: "right",
        defaultKey: "right",
        transform: "uppercase",
      },
    ],
    previewLayers: [
      { valueKey: "left", position: "leftPosition" },
      { valueKey: "middle", position: "centerPosition" },
      { valueKey: "right", position: "rightPosition" },
    ],
  },
  "Two Letter Initials": {
    labelKey: "two_letter_initials",
    mode: INITIALS,
    fields: [
      {
        key: "left",
        labelKey: "left",
        defaultKey: "left",
        transform: "uppercase",
      },
      {
        key: "right",
        labelKey: "right",
        defaultKey: "right",
        transform: "uppercase",
      },
    ],
    previewLayers: [
      { valueKey: "left", position: "twoLetterLeftPosition" },
      { valueKey: "right", position: "twoLetterRightPosition" },
    ],
  },
  "One Letter Initials": {
    labelKey: "one_letter_initials",
    mode: INITIALS,
    fields: [
      {
        key: "center",
        labelKey: "center",
        defaultKey: "center",
        transform: "uppercase",
      },
    ],
    previewLayers: [{ valueKey: "center", position: "centerPosition" }],
  },
  "Single Line": {
    labelKey: "single_line",
    mode: LINES,
    fields: [{ key: "first_line", defaultKey: "single_line" }],
  },
  "Double Line": {
    labelKey: "double_line",
    mode: LINES,
    fields: [
      {
        key: "first_line",
        labelKey: "first_line",
        defaultKey: "double_first_line",
      },
      {
        key: "second_line",
        labelKey: "second_line",
        defaultKey: "double_second_line",
      },
    ],
  },
};

/**
 * Normalise the metaobject's `monogramTypes` (a JSON-encoded string, or already
 * an array) into the configured types it allows, in selector-grid order.
 *
 * @param {string|string[]|undefined} raw monogram.monogramTypes
 * @returns {string[]} type names present in MONOGRAM_TYPES
 */
export function availableMonogramTypes(raw) {
  let allowed = raw;
  if (typeof raw === "string") {
    try {
      allowed = JSON.parse(raw);
    } catch {
      allowed = [];
    }
  }
  if (!Array.isArray(allowed)) return [];
  return Object.keys(MONOGRAM_TYPES).filter((type) => allowed.includes(type));
}

/**
 * Whether a monogram style is offered for the given monogram type. A style's
 * `monogramTypes` (a JSON-encoded string, or already an array — same format as
 * the component's monogramTypes) lists the types it supports; a style with no
 * types defined is available for every type.
 *
 * @param {string|string[]|undefined} rawTypes style.monogramTypes
 * @param {string} type selected monogram type
 * @returns {boolean}
 */
export function styleSupportsType(rawTypes, type) {
  let allowed = rawTypes;
  if (typeof rawTypes === "string") {
    try {
      allowed = JSON.parse(rawTypes);
    } catch {
      allowed = [];
    }
  }
  if (!Array.isArray(allowed) || allowed.length === 0) return true;
  return allowed.includes(type);
}

/**
 * The initial field values to seed when a type is selected, keyed by field
 * key, e.g. { left: "Z", middle: "G", right: "T" } for Three Letter Initials.
 * Pulled from locales monogram.defaults_* via each field's defaultKey.
 *
 * @param {string} type            selected monogram type
 * @param {object} defaultStrings  locales monogram.defaults
 * @returns {object}
 */
export function typeDefaults(type, defaultStrings = {}) {
  const config = MONOGRAM_TYPES[type];
  if (!config) return {};

  const defaults = {};
  config.fields.forEach((field) => {
    if (field.defaultKey && defaultStrings[field.defaultKey] != null) {
      defaults[field.key] = defaultStrings[field.defaultKey];
    }
  });
  return defaults;
}

/**
 * The fixed, type-based label shown in the accordion header aside, e.g.
 * "A - B - C" for Three Letter Initials or "Single Line". Pulled from
 * locales monogram.header_* keyed by the type's labelKey.
 *
 * @param {string} type          selected monogram type
 * @param {object} headerStrings locales monogram.header
 * @returns {string}
 */
export function monogramHeaderLabel(type, headerStrings = {}) {
  const config = MONOGRAM_TYPES[type];
  if (!config) return "";
  return headerStrings[config.labelKey] || "";
}

/**
 * Build the short summary shown in the accordion header from the entered
 * values, e.g. "Z-G-T" for initials or "GREY & SAM / Grey & sam" for lines.
 *
 * @param {string} type   selected monogram type
 * @param {object} values entered values keyed by field key
 * @returns {string}
 */
export function summarizeSelection(type, values = {}) {
  const config = MONOGRAM_TYPES[type];
  if (!config) return "";

  const parts = config.fields
    .map((field) => (values[field.key] || "").trim())
    .filter(Boolean);
  if (!parts.length) return "";

  return config.mode === INITIALS
    ? parts.join("-").toUpperCase()
    : parts.join(" / ");
}

/**
 * Flatten the customer's selection into the storable monogram shape — direct,
 * human-readable values with no metaobject ID references.
 *
 * @param {object} selection { type, values }
 * @param {object} resolved  { style, color, placement } selected metaobjects
 * @returns {object|null} e.g. { monoType, monoStyle, monoInitials, threadColor,
 *                              size, placement }, or null for an unknown type
 */
export function transformSelection(
  selection,
  { style, color, placement } = {},
) {
  const { type, values = {} } = selection || {};
  const config = MONOGRAM_TYPES[type];
  if (!config) return null;

  const fieldValues = {};
  config.fields.forEach((field, index) => {
    let value = values[field.key] || "";
    // LINES values are constrained by the selected style's text entry, so the
    // stored value matches what the preview shows even if the style changed
    // after the customer typed (which re-restricts an already-entered line).
    if (config.mode === LINES) {
      value = applyLineRestrictions(value, monogramTextForLine(style, index));
    }
    value = value.trim();
    if (value) fieldValues[field.key] = value;
  });

  const data = { monoType: type };
  if (style?.styleName) data.monoStyle = style.styleName;
  if (config.mode === INITIALS) {
    data.monoInitials = fieldValues;
  } else {
    data.monoLines = fieldValues;
  }
  if (color?.label) data.threadColor = color.label;
  if (placement?.size) data.size = placement.size;
  if (placement?.position) data.placement = placement.position;

  return data;
}

// Cart line property names for the monogram configuration, keyed by the stored
// monogram's own keys (see transformSelection). Anything not listed here is
// title-cased and used verbatim (first_line → "First Line"), so a new stored key
// needs an entry only when its label differs from that.
const MONOGRAM_PROPERTY_LABELS = {
  monoType: "Type",
  monoStyle: "Style",
  threadColor: "Thread color",
};

function monogramPropertyLabel(key) {
  return (
    MONOGRAM_PROPERTY_LABELS[key] ||
    key
      .split(/[\s_-]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")
  );
}

// The monogram line's own configuration record, hidden from the cart and
// checkout: what the cart's editor needs to reopen the modal on this monogram
// (see encodeMonogramData / monogramSelectionFromProperties).
export const MONOGRAM_DATA_PROPERTY = "_monogram_data";

// Metaobject ids are stored short (123, not gid://shopify/Metaobject/123) to
// keep the property small, and the gid rebuilds from the id alone. A thread
// color taken from a variant swatch has no metaobject, so its id is stored as is.
const SWATCH_COLOR_ID_PREFIX = "swatch:";
const isSwatchColorId = (id) => String(id).startsWith(SWATCH_COLOR_ID_PREFIX);
const shortId = (id) => {
  if (!id) return null;
  return isSwatchColorId(id) ? String(id) : window.gidToId(String(id));
};
const longId = (id) => {
  if (!id) return null;
  return isSwatchColorId(id) ? String(id) : window.idToGid(id, "Metaobject");
};

/**
 * Encode everything needed to reopen the monogram modal on a configured
 * monogram: its type, the entered values, and the ids of the selected style,
 * color and placement. The public properties carry display names only, which the
 * modal can't select by — hence the ids.
 *
 * @param {object} data      stored monogram (see transformSelection)
 * @param {object} selection the modal selection it was built from
 * @returns {string} JSON, a line item property being a single string
 */
export function encodeMonogramData(data, selection = {}) {
  return JSON.stringify({
    type: data?.monoType || null,
    values: { ...(data?.monoInitials || data?.monoLines || {}) },
    style: shortId(selection.styleId),
    color: shortId(selection.colorId),
    placement: shortId(selection.placementId),
  });
}

/**
 * Flatten a stored monogram selection into cart line-item properties. The
 * configuration is written as public properties under display names (see
 * MONOGRAM_PROPERTY_LABELS) so it reads back to the customer in the cart, in
 * checkout and on the order; nested groups (monoInitials / monoLines) are
 * flattened to their inner keys, e.g. { Type, Style, Left, Middle, Right,
 * "Thread color", Size, Placement }. Empty values are omitted.
 *
 * The hidden properties are the plumbing: `_monogram_for` links the monogram to
 * its product line, and `_monogram_data` carries the configuration in the shape
 * the cart's editor reopens it from (only written when a selection is given).
 *
 * @param {object} data      stored monogram (see transformSelection)
 * @param {string} uid       shared id linking the monogram to its product line
 * @param {object} selection the modal selection `data` was built from
 * @returns {object} string-valued properties map
 */
export function monogramLineItemProperties(data, uid, selection) {
  const properties = { _monogram_for: uid };

  const add = (key, value) => {
    if (value == null || value === "") return;
    properties[monogramPropertyLabel(key)] = String(value);
  };

  Object.entries(data || {}).forEach(([key, value]) => {
    if (value && typeof value === "object") {
      Object.entries(value).forEach(([childKey, childValue]) =>
        add(childKey, childValue),
      );
    } else {
      add(key, value);
    }
  });

  if (selection) {
    properties[MONOGRAM_DATA_PROPERTY] = encodeMonogramData(data, selection);
  }

  return properties;
}

/**
 * Whether a configured monogram is the one a cart line already holds — the
 * check that keeps closing the editor unchanged from rewriting the cart.
 *
 * Compared on the public configuration properties only. Everything hidden is
 * either derived from that configuration (the summary, the encoded record, the
 * editor's data) or regenerated per add (the bundle link), so none of it can
 * differ on its own.
 *
 * @param {object} data       configured monogram (see transformSelection)
 * @param {object} properties the line's saved properties
 * @returns {boolean}
 */
export function monogramPropertiesMatch(data, properties = {}) {
  const configuration = (entries) =>
    entries
      .filter(([key]) => !key.startsWith("_"))
      .map(([key, value]) => `${key}=${value}`)
      .sort()
      .join("|");

  return (
    configuration(Object.entries(monogramLineItemProperties(data, ""))) ===
    configuration(Object.entries(properties || {}))
  );
}

/**
 * Rebuild the modal's `selection` from a monogram line's saved properties — the
 * inverse of encodeMonogramData, used to reopen the modal on a monogram already
 * in the cart.
 *
 * Options are restored by id, so a renamed style, color or placement still comes
 * back selected. An id the component no longer offers is left as it is and the
 * modal's default-recovery effects fall back to the first available option.
 *
 * @param {object} properties monogram line item properties, keyed as saved
 * @returns {object|null} { type, values, styleId, colorId, placementId }, or
 *          null when the line carries no readable configuration
 */
export function monogramSelectionFromProperties(properties = {}) {
  const stored = parseMonogramData(properties?.[MONOGRAM_DATA_PROPERTY]);
  const config = MONOGRAM_TYPES[stored?.type];
  if (!config) return null;

  // Every field of the type is set, blank included: a value the shopper cleared
  // isn't saved, and without it the modal would seed its default back in.
  const values = {};
  config.fields.forEach(({ key }) => {
    values[key] = stored.values?.[key] || "";
  });

  return {
    type: stored.type,
    values,
    styleId: longId(stored.style),
    colorId: longId(stored.color),
    placementId: longId(stored.placement),
  };
}

function parseMonogramData(value) {
  if (!value) return null;
  try {
    const stored = JSON.parse(value);
    return stored && typeof stored === "object" ? stored : null;
  } catch {
    console.warn("[monogram] Unreadable monogram data on a cart line.", value);
    return null;
  }
}

// Display labels for the per-field monogram values, used when encoding the
// selection.
const MONOGRAM_FIELD_LABELS = {
  left: "Left",
  middle: "Middle",
  right: "Right",
  center: "Middle",
  first_line: "Line 1",
  second_line: "Line 2",
};

function monogramFieldLabel(key) {
  return (
    MONOGRAM_FIELD_LABELS[key] ||
    key
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")
  );
}

/**
 * Short, human-readable recap of a configured monogram, e.g.
 * "2061 / ABC / Blue" — style number, the entered initials (joined) or line
 * text (joined by " / "), then thread color. Built from the flat, ID-free
 * monogram data (see transformSelection); only the parts present are shown.
 * Shown to the customer on the product form and attached to the parent product
 * cart line as its public "Monogram" property.
 *
 * @param {object} data stored monogram (see transformSelection)
 * @returns {string}
 */
export function monogramSummary(data) {
  if (!data) return "";

  const text = data.monoInitials
    ? Object.values(data.monoInitials).join("")
    : data.monoLines
      ? Object.values(data.monoLines).join(" / ")
      : "";

  return [data.monoStyle, text, data.threadColor].filter(Boolean).join(" / ");
}

/**
 * Encode the full monogram selection into a single human-readable string for the
 * monogram cart line's hidden `_monogram` property — the order-fulfilment record
 * of everything the customer chose, tied to the parent product it personalises.
 *
 * Format (segments joined by "; ", with a trailing "; "):
 *   {title} SKU: {sku}; Mono Style: {style}; Mono Initials: Left: G, Middle: O,
 *   Right: K; Thread Color: {color}; Size: {size}; Placement: {placement};
 *
 * INITIALS types emit a "Mono Initials" segment (Left/Middle/Right/Center as
 * configured); LINES types emit a "Mono Lines" segment (Line 1/Line 2). Empty
 * parts are omitted, so single-line monograms emit a single "Line 1" entry.
 *
 * @param {object} data stored monogram (see transformSelection)
 * @param {{ productTitle?: string, sku?: string }} parent the personalised
 *        product's title and selected-variant SKU
 * @returns {string}
 */
export function encodeMonogram(data, { productTitle = "", sku = "" } = {}) {
  if (!data) return "";

  const segments = [];

  const lead = [productTitle, sku && `SKU: ${sku}`].filter(Boolean).join(" ");
  if (lead) segments.push(lead);

  if (data.monoStyle) segments.push(`Mono Style: ${data.monoStyle}`);

  const fieldSegment = (label, group) => {
    const parts = Object.entries(group || {})
      .filter(([, value]) => value != null && value !== "")
      .map(([key, value]) => `${monogramFieldLabel(key)}: ${value}`);
    if (parts.length) segments.push(`${label}: ${parts.join(", ")}`);
  };

  fieldSegment("Mono Initials", data.monoInitials);
  fieldSegment("Mono Lines", data.monoLines);

  if (data.threadColor) segments.push(`Thread Color: ${data.threadColor}`);
  if (data.size) segments.push(`Size: ${data.size}`);
  if (data.placement) segments.push(`Placement: ${data.placement}`);

  return segments.length ? `${segments.join("; ")}; ` : "";
}

/**
 * The monogram_component handle to offer for a variant — its own
 * custom.monogram metafield, else its product's. A component without a
 * monogramProduct is skipped: there is nothing to price the monogram from (see
 * findMonogramVariant), so offering it would only lead to a dead end.
 *
 * @param {object} variant selected product variant, carrying its product back-reference
 * @returns {string|null} the handle, or null when none is offerable
 */
export function monogramHandleFor(variant) {
  const component = [variant?.monogram, variant?.product?.monogram].find(
    (candidate) => candidate?.handle && candidate?.monogramProduct,
  );

  return component?.handle || null;
}

/**
 * Find the monogram pricing variant for the current selection. Monograms are
 * priced off the `monogramProduct` (a Class × Height matrix): the variant whose
 * "Class" option matches the selected style's `pricingClass` and whose "Height"
 * matches the selected placement's `pricingHeight`.
 *
 * @param {object} product   monogram.monogramProduct (with its variants)
 * @param {object} style     selected monogram style (carries pricingClass)
 * @param {object} placement selected placement (carries pricingHeight)
 * @returns {object|null} the matching product variant, or null
 */
export function findMonogramVariant(product, style, placement) {
  const pricingClass = style?.pricingClass;
  const pricingHeight = placement?.pricingHeight;
  if (pricingClass == null || pricingHeight == null) return null;

  return (
    (product?.variants || []).find((variant) => {
      const options = variant.selectedOptions || [];
      const matches = (name, target) =>
        options.some(
          (option) => option.name === name && option.value === String(target),
        );
      return matches("Class", pricingClass) && matches("Height", pricingHeight);
    }) || null
  );
}

/**
 * Derive the host product's color context for the thread-color groupings from
 * the selected variant. The variant back-references its full product (see
 * addProductHelpers), so everything needed lives on it:
 *
 *  - currentColor:        the variant's value for the color option (the one
 *                         rendered as swatches), matched against thread color
 *                         names to find the matching thread color.
 *  - matchingThreadColor: the variant's custom.matching_thread_color color
 *                         metaobject, when set — an explicit override that wins
 *                         over name matching.
 *  - styleColors:         one thread color per product variant (see
 *                         variantThreadColor), in variant order.
 *  - styleName:           the product's style category name, used for the
 *                         "{style} Style Colors" heading.
 *
 * @param {object} variant the selected product variant
 * @returns {{ currentColor: string|null, matchingThreadColor: object|null, styleColors: object[], styleName: string }}
 */
export function monogramColorContext(variant) {
  const product = variant?.product;
  const colorOption = product?.options?.find((option) =>
    option.optionValues.some((optionValue) => optionValue.swatch),
  );
  const colorName = (candidate) =>
    candidate?.selectedOptions?.find(
      (option) => option.name === colorOption?.name,
    )?.value || null;
  const swatchHexes = new Map(
    (colorOption?.optionValues || []).map((optionValue) => [
      optionValue.name,
      optionValue.swatch?.color,
    ]),
  );

  return {
    currentColor: colorName(variant),
    matchingThreadColor: variant?.matchingThreadColor || null,
    styleColors: (product?.variants || [])
      .map((candidate) =>
        variantThreadColor(candidate, colorName(candidate), swatchHexes),
      )
      .filter(Boolean),
    styleName: product?.styleCategory?.styleCategoryName || "",
  };
}

/**
 * The thread color a product variant contributes to the style colors:
 *
 *  - its custom.matching_thread_color metaobject, when set;
 *  - otherwise its color option value as a thread color, from the swatch's hex
 *    and the value's name (the swatch image is ignored);
 *  - otherwise, with no swatch hex, none.
 *
 * @param {object} variant     a product variant
 * @param {string} colorName   the variant's color option value
 * @param {Map}    swatchHexes swatch hex by color option value
 * @returns {object|null}
 */
function variantThreadColor(variant, colorName, swatchHexes) {
  if (variant?.matchingThreadColor) return variant.matchingThreadColor;
  const hex = swatchHexes.get(colorName);
  if (!hex) return null;
  return {
    id: `${SWATCH_COLOR_ID_PREFIX}${colorName}`,
    label: colorName,
    color: hex,
  };
}

const normalizeColorName = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

/**
 * Name-matching rules used to find a thread color for a variant's color option
 * value, in priority order — the first rule to produce a match wins. Each rule
 * takes the raw variant color name and returns the candidate thread color names
 * to compare against (comparison is trimmed and case-insensitive, so rules only
 * need to handle splitting).
 *
 * Add a rule to widen matching; keep looser rules later in the list.
 */
export const COLOR_MATCH_RULES = [
  // The whole value, e.g. "Red" → "Red".
  (name) => [name],
  // Compound values match on everything after the "&", e.g. "White & Red" → "Red".
  (name) => (name.includes("&") ? name.split("&").slice(1) : []),
];

/**
 * Find the thread color matching a variant's color option value by name, trying
 * each rule in COLOR_MATCH_RULES against each pool of colors in turn. Rules are
 * the outer loop, so a tighter match in a later pool beats a looser match in an
 * earlier one; within a rule, the earlier pool wins.
 *
 * @param {string}     colorName the variant's color option value
 * @param {object[][]} pools     color lists in priority order (style, then additional)
 * @returns {object|null}
 */
export function matchThreadColorByName(colorName, pools = []) {
  if (!colorName) return null;

  for (const rule of COLOR_MATCH_RULES) {
    const candidates = rule(colorName).map(normalizeColorName).filter(Boolean);
    if (!candidates.length) continue;

    for (const pool of pools) {
      const match = pool.find((color) =>
        candidates.includes(normalizeColorName(color.label)),
      );
      if (match) return match;
    }
  }

  return null;
}

/**
 * Resolve the variant's matching thread color:
 *
 *  1. the variant's custom.matching_thread_color metafield, when set;
 *  2. otherwise the color whose name matches the variant's color option value
 *     (see matchThreadColorByName), preferring style colors over additional.
 *
 * @param {object}   context the variant's monogramColorContext
 * @param {object[]} colors  the global (additional) monogram colors
 * @returns {object|null}
 */
export function resolveMatchingThreadColor(context = {}, colors = []) {
  const { matchingThreadColor, currentColor, styleColors = [] } = context;
  if (matchingThreadColor) return matchingThreadColor;
  return matchThreadColorByName(currentColor, [styleColors, colors]);
}

/**
 * Split the available thread colors into the three display groups, in the order
 * they render in the color selector:
 *
 *  - matching:   the resolved matching thread color (see resolveMatchingThreadColor).
 *  - style:      the product's variant colors (see monogramColorContext).
 *  - additional: the global monogram colors.
 *
 * Colors are deduplicated by name and by hex across the groups in that same
 * priority order, so a color appears exactly once — in the highest-priority
 * group that offers it.
 *
 * The concatenation `[...matching, ...style, ...additional]` is the canonical
 * display order — the first entry is the default selection.
 *
 * @param {object[]} colors        global monogram colors (with `label`)
 * @param {object[]} styleColors   the product's variant colors
 * @param {object}   matchingColor the resolved matching thread color, if any
 * @returns {{ matching: object[], style: object[], additional: object[] }}
 */
export function groupMonogramColors(
  colors = [],
  styleColors = [],
  matchingColor = null,
) {
  const seenNames = new Set();
  const seenHexes = new Set();
  const dedupe = (list) =>
    list.filter((color) => {
      const name = normalizeColorName(color?.label);
      const hex = normalizeColorName(color?.color);
      if (!name || seenNames.has(name) || seenHexes.has(hex)) return false;
      seenNames.add(name);
      if (hex) seenHexes.add(hex);
      return true;
    });

  return {
    matching: dedupe(matchingColor ? [matchingColor] : []),
    style: dedupe(styleColors),
    additional: dedupe(colors),
  };
}

/**
 * Format a monogram pricing variant's price for display, e.g. "$61.00". Returns
 * an empty string when no variant resolves.
 *
 * @param {object} variant a monogramProduct variant
 * @returns {string}
 */
export function formatMonogramPrice(variant) {
  if (!variant?.price) return "";
  return window.formatMoney(
    parseFloat(variant.price.amount),
    false,
    variant.price.currencyCode,
  );
}

/**
 * Resolve the overlay layers for the live preview: one entry per entered
 * initial, in stacking order, each pointing at the letter's position SVG on the
 * selected style. Letters with no entered value, no matching style letter, or
 * no image for the required position are dropped. Only INITIALS types have
 * `previewLayers`; line types (and any unconfigured type) return [].
 *
 * When the style carries an optional `border` image, it is added as a first
 * layer (drawn beneath the letters). The border SVG shares the letters' viewBox
 * and is fetched/recolored/overlaid exactly like a letter; it is only included
 * alongside at least one rendered letter, so a clear selection shows nothing,
 * and only for the types in the style's `borderAppliesTo` (all types if blank).
 *
 * @param {string} type   selected monogram type
 * @param {object} values entered values keyed by field key
 * @param {object} style  selected monogram style (with its `letters` and `border`)
 * @returns {{ key: string, letter: string, url: string }[]}
 */
export function monogramPreviewLayers(type, values = {}, style) {
  const config = MONOGRAM_TYPES[type];
  if (!config || !config.previewLayers || !style) return [];

  const letters = style.letters || [];
  const letterLayers = config.previewLayers
    .map(({ valueKey, position }) => {
      const letter = (values[valueKey] || "").trim().toUpperCase();
      if (!letter) return null;

      const match = letters.find(
        (entry) => (entry.letter || "").toUpperCase() === letter,
      );
      if (!match) {
        console.warn(
          `[monogram] No "${letter}" letter found for style "${
            style.styleName || style.handle || style.id
          }" — skipping its preview layer.`,
        );
        return null;
      }

      const url = match[position]?.image?.url;
      if (!url) {
        console.warn(
          `[monogram] Letter "${letter}" for style "${
            style.styleName || style.handle || style.id
          }" is missing its "${position}" image — skipping its preview layer.`,
        );
        return null;
      }

      return { key: valueKey, letter, url };
    })
    .filter(Boolean);

  const borderUrl = style.border?.image?.url;
  if (
    !borderUrl ||
    !letterLayers.length ||
    !styleSupportsType(style.borderAppliesTo, type)
  ) {
    return letterLayers;
  }

  return [{ key: "border", letter: "", url: borderUrl }, ...letterLayers];
}

/**
 * Interpret a metaobject boolean field (a "true"/"false" string, or already a
 * boolean) as a flag.
 *
 * @param {string|boolean|undefined} value
 * @returns {boolean}
 */
export function monogramFlag(value) {
  return value === true || value === "true";
}

/**
 * Apply a text entry's input restrictions to a raw line value so the
 * personalization field and the live preview always agree:
 *   - `lettersOnly` strips everything but letters and spaces (e.g. & or #).
 *   - `uppercase` forces the value uppercase.
 * A missing entry leaves the value untouched.
 *
 * @param {string} value raw entered line value
 * @param {object} entry the line's monogram_text entry
 * @returns {string}
 */
export function applyLineRestrictions(value, entry) {
  let next = value || "";
  if (monogramFlag(entry?.lettersOnly)) {
    next = next.replace(/[^\p{L}\s]/gu, "");
  }
  if (monogramFlag(entry?.uppercase)) {
    next = next.toUpperCase();
  }
  return next;
}

/**
 * Resolve the `monogram_text` entry governing a given preview line. Each line is
 * styled by the style's matching text entry (line 0 → text[0], line 1 →
 * text[1], …); when there are fewer entries than lines the last entry is reused
 * for any additional lines.
 *
 * @param {object} style     selected monogram style (with its `text` array)
 * @param {number} lineIndex zero-based line index
 * @returns {object|null}
 */
export function monogramTextForLine(style, lineIndex) {
  const text = style?.text || [];
  if (!text.length) return null;
  return text[lineIndex] || text[text.length - 1];
}

/**
 * Re-apply each LINES field's input restrictions to the already-stored values so
 * they stay in sync after the customer switches type or style. INITIALS types
 * are returned untouched, and the same `values` reference is returned when
 * nothing changes so callers can skip a needless state update.
 *
 * @param {string} type   selected monogram type
 * @param {object} values entered values keyed by field key
 * @param {object} style  selected monogram style (with its `text` array)
 * @returns {object} values with line restrictions applied
 */
export function restrictLineValues(type, values = {}, style) {
  const config = MONOGRAM_TYPES[type];
  if (!config || config.mode !== LINES) return values;

  let changed = false;
  const next = { ...values };
  config.fields.forEach((field, index) => {
    const current = values[field.key];
    if (current == null || current === "") return;
    const restricted = applyLineRestrictions(
      current,
      monogramTextForLine(style, index),
    );
    if (restricted !== current) {
      next[field.key] = restricted;
      changed = true;
    }
  });
  return changed ? next : values;
}

/**
 * Resolve the live-text preview lines for a LINES monogram type: one entry per
 * field, in order, pairing the restricted entered value with the text entry
 * that styles it. Lines with no entered value (after restrictions) are dropped,
 * so the stacked preview only reserves space for filled lines. INITIALS types,
 * styles with no text entries, and unconfigured types return [].
 *
 * @param {string} type   selected monogram type
 * @param {object} values entered values keyed by field key
 * @param {object} style  selected monogram style (with its `text` array)
 * @returns {{ key: string, text: string, entry: object }[]}
 */
export function monogramTextPreviewLines(type, values = {}, style) {
  const config = MONOGRAM_TYPES[type];
  if (!config || config.mode !== LINES || !style) return [];

  return config.fields
    .map((field, index) => {
      const entry = monogramTextForLine(style, index);
      if (!entry) return null;
      const text = applyLineRestrictions(values[field.key] || "", entry);
      if (!text.trim()) return null;
      return { key: field.key, text, entry };
    })
    .filter(Boolean);
}
